'use client'

import React from 'react'
import { useRouter } from 'next/navigation'

import { type AffiliateTile as AffiliateTileType } from '@rewardops/utils'

import { useTranslation } from '~/app/i18n/client'
import ResponsiveComponent from '~/components/responsive-component'

type AffiliateTileProps = {
  content: AffiliateTileType
  onDismiss?: () => void
}

const AffiliateTile: React.FC<AffiliateTileProps> = ({ content, onDismiss }) => {
  const { t } = useTranslation()
  const router = useRouter()

  const {
    title,
    ctaText,
    link,
    heroImage,
    logoImage,
    backgroundColor,
    textColor,
    tileImageOnly,
  } = content

  const displayTitle = title || t('affiliateTile.defaultTitle')
  const displayCta = ctaText || t('affiliateTile.defaultCta')

  const onClick = () => {
    if (!link) return

    if (link?.isRelativeUrl) {
      router.push(link.to)
    } else {
      window?.open(link.to, '_blank')
    }
  }

  const backgroundStyle: React.CSSProperties = {
    backgroundColor: backgroundColor || undefined,
    ...(heroImage?.file?.url && {
      backgroundImage: `url(${heroImage.file.url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }),
  }

  const tileContent = tileImageOnly ? null : (
    <>
      <div className="flex flex-1 flex-col justify-between p-4">
        {logoImage?.file?.url && (
          <img
            src={logoImage.file.url}
            alt=""
            className="h-8 w-auto self-start object-contain"
          />
        )}
      </div>
      <div className="flex flex-col gap-1 p-4">
        <p
          className="text-sm font-semibold"
          style={{ color: textColor || '#ffffff' }}
        >
          {displayTitle}
        </p>
        {displayCta && (
          <p
            className="text-xs"
            style={{ color: textColor || '#ffffff' }}
          >
            {displayCta}
          </p>
        )}
      </div>
    </>
  )

  const dismissColor = textColor || '#ffffff'

  const desktopComponent = (
    <div
      onClick={onClick}
      className="relative flex h-[331px] cursor-pointer flex-col overflow-hidden rounded-lg border border-zinc-200 hover:shadow-[0_3px_8px_0px_rgba(0,0,0,0.2)] md:h-[350px]"
      style={backgroundStyle}
    >
      {onDismiss && (
        <button
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/30 text-lg hover:bg-black/50"
          style={{ color: dismissColor }}
          onClick={(e) => {
            e.stopPropagation()
            onDismiss()
          }}
          aria-label="Dismiss tile"
        >
          &times;
        </button>
      )}
      {tileContent}
    </div>
  )

  const mobileComponent = (
    <div
      onClick={onClick}
      className="relative flex h-[331px] cursor-pointer flex-col overflow-hidden border border-zinc-200"
      style={backgroundStyle}
    >
      {onDismiss && (
        <button
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/30 text-lg hover:bg-black/50"
          style={{ color: dismissColor }}
          onClick={(e) => {
            e.stopPropagation()
            onDismiss()
          }}
          aria-label="Dismiss tile"
        >
          &times;
        </button>
      )}
      {tileContent}
    </div>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { AffiliateTile }
